import React, { useCallback, useEffect, useState } from 'react';
import { Archive, RefreshCw, Download, RotateCcw, Eye, X } from 'lucide-react';
import { optionalVouchersApi, OptionalVoucherRow } from '../services/api';
import { useToast } from '../components/Toast/Toast';

/**
 * Optional Vouchers — what deleting a voucher does now. Each one keeps its
 * number, counts in no report or balance, and is marked Optional in Tally.
 * Restore puts it back exactly as it was (and Tally makes it regular again).
 */

const inr = (n: number | null | undefined) =>
  n === null || n === undefined ? '—' : Number(n).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const TALLY: Record<string, { label: string; cls: string }> = {
  pending: { label: 'Waiting for the bridge', cls: 'bg-amber-50 text-amber-800 border-amber-200' },
  done: { label: 'Optional in Tally', cls: 'bg-emerald-50 text-emerald-800 border-emerald-200' },
  not_in_tally: { label: 'Not in Tally', cls: 'bg-gray-50 text-gray-600 border-gray-200' },
};

const OptionalVouchers: React.FC = () => {
  const { showError, showSuccess } = useToast();
  const [rows, setRows] = useState<OptionalVoucherRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [search, setSearch] = useState('');
  const [view, setView] = useState<any | null>(null);
  const [busy, setBusy] = useState<number | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { setRows((await optionalVouchersApi.list({ from, to, search: search.trim() })).data || []); }
    catch (e: any) { showError('Could not load optional vouchers', e?.message); }
    finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [from, to, search]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const open = async (id: number) => {
    try { setView((await optionalVouchersApi.detail(id)).data); }
    catch (e: any) { showError('Could not open', e?.message); }
  };
  const restore = async (r: OptionalVoucherRow) => {
    if (!window.confirm(`Restore ${r.vch_type || ''} ${r.vch_no || ''}?\n\nIt goes back into the books exactly as it was and counts in every report again; in Tally it becomes a regular voucher again.`)) return;
    setBusy(r.vch_id);
    try { await optionalVouchersApi.restore(r.vch_id); showSuccess('Restored', `${r.vch_no || 'Voucher'} is back in the books`); setView(null); load(); }
    catch (e: any) { showError('Could not restore', e?.message); }
    finally { setBusy(null); }
  };
  const exportExcel = async () => {
    const { utils, writeFile } = await import('xlsx');
    const wb = utils.book_new();
    utils.book_append_sheet(wb, utils.json_to_sheet(rows.map(r => ({
      Date: r.vch_date, Type: r.vch_type, 'Vch No': r.vch_no, Party: r.party, Amount: Number(r.amount),
      Reason: r.reason, 'Made optional by': r.made_by, 'Made optional at': r.made_at, Tally: TALLY[r.tally_status]?.label || r.tally_status,
    }))), 'Optional vouchers');
    writeFile(wb, `Optional_Vouchers_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };
  const total = rows.reduce((s, r) => s + Number(r.amount || 0), 0);

  return (
    <div className="min-h-screen bg-gray-100 p-3">
      <div className="bg-white rounded-lg shadow p-4 max-w-[1300px] mx-auto">
        <div className="flex flex-wrap items-center gap-2 mb-3">
          <Archive className="h-5 w-5 text-blue-600" />
          <h1 className="text-lg font-semibold text-gray-800 flex-1">Optional Vouchers</h1>
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Vch no, party or reason" className="px-2 py-1.5 text-sm border border-gray-300 rounded w-56" />
          <input type="date" value={from} onChange={e => setFrom(e.target.value)} className="px-2 py-1.5 text-sm border border-gray-300 rounded" />
          <span className="text-gray-400 text-sm">to</span>
          <input type="date" value={to} min={from} onChange={e => setTo(e.target.value)} className="px-2 py-1.5 text-sm border border-gray-300 rounded" />
          <button onClick={exportExcel} disabled={!rows.length} className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs rounded border border-gray-300 hover:bg-gray-50 disabled:opacity-50"><Download className="h-3.5 w-3.5" /> Excel</button>
          <button onClick={load} disabled={loading} className="p-2 rounded border border-gray-300 text-gray-500 hover:bg-gray-50" title="Refresh"><RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} /></button>
        </div>
        <p className="text-xs text-gray-500 mb-3">
          Deleting a voucher makes it <strong>Optional</strong>: it keeps its number, counts in no report or balance, and is marked Optional in Tally.
          Restore puts it back exactly as it was.
        </p>

        <div className="overflow-auto border border-gray-200 rounded max-h-[calc(100vh-220px)]">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-[11px] uppercase text-gray-500 sticky top-0">
              <tr>
                <th className="px-2 py-2 text-left">Date</th><th className="px-2 py-2 text-left">Type</th><th className="px-2 py-2 text-left">Vch No</th>
                <th className="px-2 py-2 text-left">Party</th><th className="px-2 py-2 text-right">Amount</th><th className="px-2 py-2 text-left">Reason</th>
                <th className="px-2 py-2 text-left">Made optional</th><th className="px-2 py-2 text-left">Tally</th><th className="px-2 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {rows.length === 0 ? (
                <tr><td colSpan={9} className="px-3 py-10 text-center text-gray-400">{loading ? 'Loading…' : 'No optional vouchers.'}</td></tr>
              ) : rows.map(r => (
                <tr key={r.vch_id} className="hover:bg-gray-50">
                  <td className="px-2 py-1.5 whitespace-nowrap">{r.vch_date}</td>
                  <td className="px-2 py-1.5">{r.vch_type}</td>
                  <td className="px-2 py-1.5 font-semibold whitespace-nowrap">{r.vch_no || '—'}</td>
                  <td className="px-2 py-1.5">{r.party}</td>
                  <td className="px-2 py-1.5 text-right tabular-nums">{inr(r.amount)}</td>
                  <td className="px-2 py-1.5 text-gray-600">{r.reason || <span className="text-gray-300">—</span>}</td>
                  <td className="px-2 py-1.5 text-xs text-gray-500 whitespace-nowrap">{r.made_at}<br />{r.made_by}</td>
                  <td className="px-2 py-1.5"><span className={`px-1.5 py-0.5 rounded border text-[11px] ${TALLY[r.tally_status]?.cls || ''}`}>{TALLY[r.tally_status]?.label || r.tally_status}</span></td>
                  <td className="px-2 py-1.5 text-right whitespace-nowrap">
                    <button onClick={() => open(r.vch_id)} className="inline-flex items-center gap-0.5 text-blue-600 hover:underline mr-3"><Eye className="h-3.5 w-3.5" /> View</button>
                    <button onClick={() => restore(r)} disabled={busy === r.vch_id} className="inline-flex items-center gap-0.5 text-emerald-700 hover:underline disabled:opacity-50"><RotateCcw className="h-3.5 w-3.5" /> Restore</button>
                  </td>
                </tr>
              ))}
            </tbody>
            {rows.length > 0 && (
              <tfoot className="bg-gray-50 font-semibold"><tr><td className="px-2 py-2" colSpan={4}>{rows.length} optional voucher(s)</td><td className="px-2 py-2 text-right tabular-nums">{inr(total)}</td><td colSpan={4}></td></tr></tfoot>
            )}
          </table>
        </div>
      </div>

      {view && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setView(null)}>
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl p-5 max-h-[85vh] overflow-auto" onClick={e => e.stopPropagation()}>
            <div className="flex items-start gap-2 mb-3">
              <div className="flex-1">
                <h3 className="font-semibold text-gray-800">Optional voucher {view.vch_no || ''}</h3>
                <div className="text-xs text-gray-500">{String(view.vch_date || '').slice(0, 10)} · ₹{inr(view.amount)} · made optional {String(view.made_at || '').slice(0, 16)} by {view.made_by}{view.reason ? ` — ${view.reason}` : ''}</div>
              </div>
              <button onClick={() => setView(null)} className="p-1 rounded hover:bg-gray-100"><X className="h-4 w-4" /></button>
            </div>
            <table className="w-full text-sm border border-gray-200 rounded">
              <thead className="bg-gray-50 text-[11px] uppercase text-gray-500"><tr><th className="px-2 py-1.5 text-left">Ledger / item</th><th className="px-2 py-1.5 text-right">Dr</th><th className="px-2 py-1.5 text-right">Cr</th></tr></thead>
              <tbody className="divide-y divide-gray-100">
                {view.lines.map((l: any, i: number) => (
                  <React.Fragment key={i}>
                    <tr><td className="px-2 py-1.5 font-medium">{l.ledger}</td><td className="px-2 py-1.5 text-right tabular-nums">{l.amount > 0 ? inr(l.amount) : ''}</td><td className="px-2 py-1.5 text-right tabular-nums">{l.amount < 0 ? inr(-l.amount) : ''}</td></tr>
                    {l.items.map((it: any, j: number) => (
                      <tr key={j} className="text-xs text-gray-500"><td className="px-2 py-1 pl-6" colSpan={3}>{it.item} — {Math.abs(it.qty)} × ₹{inr(it.rate)} = ₹{inr(Math.abs(it.amount))}</td></tr>
                    ))}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
            {view.remark && <p className="text-xs text-gray-500 mt-2">Narration: {view.remark}</p>}
            <div className="flex justify-end mt-4">
              <button onClick={() => restore({ vch_id: view.vch_id, vch_no: view.vch_no } as any)} className="inline-flex items-center gap-1 px-3 py-1.5 text-sm rounded bg-emerald-600 text-white hover:bg-emerald-700"><RotateCcw className="h-4 w-4" /> Restore</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OptionalVouchers;
